import { styled } from '@mui/material';
import { Grid } from '@mui/material';
import Typography from '@mui/material/Typography';
import AppAccordion from 'components/AppAccordion';
import Heading from 'components/Heading';
import { Row } from 'components/Layout';
import {
  numberToCurrencyString,
  numberToDisplayString,
} from 'further-ui/utils';
import { HoldingClassRealisations } from '../../types';
import { sumBy } from 'lodash';
import PerInvestorAllocationTable from '../../AllocationTables/PerInvestorAllocationTable';
import PerClassAllocationTable from '../../AllocationTables/PerClassAllocationTable';
import { CurrencyCode } from 'further-types/lp/vehicle';
import Spacing from 'components/Spacing';

type Props = {
  currency: CurrencyCode;
  holdingClassRealisations: HoldingClassRealisations;
  investorAllocations?: Array<{
    commitmentId: string;
    investorName: string;
    receivedConsideration: number;
    escrowConsideration: number;
    earnoutConsideration: number;
    withholdingTaxPayable: number;
    allocationPercentage: number;
  }>;
};

const RealisationInfo = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.secondary,
  fontSize: theme.typography.pxToRem(18),
  alignSelf: 'center',
}));

const ConsiderationOverview: React.FC<Props> = ({
  currency,
  holdingClassRealisations,
  investorAllocations,
}) => {
  const totalUnits = sumBy(holdingClassRealisations, 'unitsSold');
  const totalCashConsideration = holdingClassRealisations.reduce(
    (acc, holdingClassRealisation) => {
      return (
        acc +
        (holdingClassRealisation.cashConsiderationAmounts
          ?.receivedConsiderationTotal ?? 0) +
        (holdingClassRealisation.cashConsiderationAmounts
          ?.earnoutConsiderationTotal ?? 0) +
        (holdingClassRealisation.cashConsiderationAmounts
          ?.escrowConsiderationTotal ?? 0) +
        (holdingClassRealisation.cashConsiderationAmounts
          ?.withholdingTaxPayable ?? 0)
      );
    },
    0,
  );
  return (
    <Grid size={{ xs: 12, md: 8 }}>
      <Heading variant="h2">Consideration overview</Heading>
      <AppAccordion title="Cash consideration">
        <AppAccordion.Summary>
          <Row spacing="xs">
            <RealisationInfo>
              {numberToDisplayString(totalUnits)} Units |{' '}
              {numberToCurrencyString(totalCashConsideration, {
                currency,
              })}{' '}
              value
            </RealisationInfo>
          </Row>
        </AppAccordion.Summary>
        <AppAccordion.Content>
          <Spacing size="md">
            <Heading variant="h2" noMargin>
              Per class allocation
            </Heading>
            <PerClassAllocationTable
              holdingClassRealisations={holdingClassRealisations}
              currency={currency}
            />
            <Heading variant="h2" noMargin>
              Per investor allocation
            </Heading>
            <PerInvestorAllocationTable
              currency={currency}
              investorAllocations={investorAllocations ?? []}
            />
          </Spacing>
        </AppAccordion.Content>
      </AppAccordion>
    </Grid>
  );
};

export default ConsiderationOverview;
